import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Form, Modal, Input, TreeSelect, Typography } from "antd";
import { useDispatch } from "react-redux";
import {
  buildFolderDestinationOptions,
  ROOT_DESTINATION,
} from "./documentMoveUtils";
import {
  createAssemblyJob,
  pollAssemblyJob,
  MAX_ASSEMBLY_ITEMS,
} from "../../service/assemblyService";
import { getDocumentDetail } from "../../service/matter";
import {
  fetchMatterDetail,
  fetchMatterDetailList,
  postHighlightedMatter,
} from "../../slice/MatterDetailsSlice";
import { firstSerializerError } from "../FileMergeModal/FileMergeModalContainerUtils";
import { appendAIPaneFromCurrentUrl } from "../../utils/aiPaneUrl";
import MergeProgressNotification from "../FileMergeModal/MergeProgressNotification";

const { Text } = Typography;

// Default output name when the user leaves the field blank: readable and
// unique enough ("Combined 2026-08-11 1536.pdf"). Mirrors the document
// table's "Merge selected" default naming.
const defaultOutputName = (now = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `Combined ${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate()
  )} ${pad(now.getHours())}${pad(now.getMinutes())}.pdf`;
};

// The tree's "current folder" (websocket.currentFolderId) is a bare id, or
// "root"/null at the matter root; buildFolderDestinationOptions's values are
// bare ids too (see documentMoveUtils.js), so both need the same shape.
const normalizeFolderId = (rawFolderId) => {
  if (rawFolderId == null || rawFolderId === "" || rawFolderId === "root") {
    return ROOT_DESTINATION;
  }
  const value = String(rawFolderId);
  return value.startsWith("F") ? value.slice(1) : value;
};

// "Combine files" — the sidebar tree's multi-select counterpart to the
// document table's "Merge selected", except this one asks explicitly for a
// name and a destination instead of silently dropping the output into
// whatever folder happens to be open. Owned by TreeComponent (mounted once)
// rather than the per-row DocumentContextMenu that requests it, so the
// progress toast below survives a tree refresh that lands mid-job.
const CombineFilesModal = ({
  open,
  onClose,
  documents = [],
  matterId,
  rawTree = null,
  currentFolderId = null,
  listRefreshArgs = {},
  navigate,
}) => {
  const dispatch = useDispatch();
  const [outputName, setOutputName] = useState("");
  const [destination, setDestination] = useState(ROOT_DESTINATION);
  const [submitError, setSubmitError] = useState("");

  const [isCombining, setIsCombining] = useState(false);
  const [combineProgress, setCombineProgress] = useState(0);
  const [isCombineComplete, setIsCombineComplete] = useState(false);
  const [combinedFileUrl, setCombinedFileUrl] = useState(null);
  const [combinedFile, setCombinedFile] = useState(null);
  const [combineError, setCombineError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setOutputName("");
    setDestination(normalizeFolderId(currentFolderId));
    setSubmitError("");
  }, [open, currentFolderId]);

  const folderOptions = useMemo(
    () => buildFolderDestinationOptions(rawTree, null),
    [rawTree]
  );

  const handleCombine = useCallback(async () => {
    if (isCombining) return;
    if (documents.length < 2) {
      setSubmitError("At least two files are required to combine.");
      return;
    }
    if (documents.length > MAX_ASSEMBLY_ITEMS) {
      setSubmitError(
        `You can combine up to ${MAX_ASSEMBLY_ITEMS} files at a time — remove ${
          documents.length - MAX_ASSEMBLY_ITEMS
        } and try again.`
      );
      return;
    }
    if (!matterId) {
      setSubmitError("Couldn't determine the matter — try again.");
      return;
    }

    const trimmed = (outputName || "").trim();
    const finalOutputName = trimmed
      ? trimmed.toLowerCase().endsWith(".pdf")
        ? trimmed
        : `${trimmed}.pdf`
      : defaultOutputName();

    const toRoot = !destination || destination === ROOT_DESTINATION;
    const folder = toRoot ? undefined : `F${destination}`;
    const items = documents.map((doc) => ({ document_id: String(doc.id) }));

    // Close the naming/destination modal right away — the floating
    // notification below takes over while the job runs server-side, same
    // pattern as the document-table "Merge selected" flow.
    onClose();
    setIsCombining(true);
    setIsCombineComplete(false);
    setCombineProgress(0);
    setCombinedFileUrl(null);
    setCombineError(null);

    try {
      const { job_id: jobId } = await createAssemblyJob({
        matter: matterId,
        outputName: finalOutputName,
        folder,
        items,
      });

      let simulated = 15;
      const job = await pollAssemblyJob(jobId, {
        onTick: (tick) => {
          if (tick.status === "queued") {
            setCombineProgress(15);
          } else if (tick.status === "running") {
            simulated = Math.min(simulated + 7, 90);
            setCombineProgress(simulated);
          }
        },
      });

      if (job.status === "failed") {
        throw new Error(job.error_detail || "Failed to combine files");
      }

      const detailResponse = await getDocumentDetail(job.output_document_id);
      const mergedDocument = detailResponse?.data;
      if (!mergedDocument?.relative_path) {
        throw new Error(
          "Combine finished but the new file could not be loaded — refresh the matter to find it."
        );
      }

      dispatch(fetchMatterDetail(matterId));
      dispatch(
        fetchMatterDetailList({
          id: matterId,
          page: listRefreshArgs?.currentPage || 1,
          sort_direction: listRefreshArgs?.sort,
          sort_by: listRefreshArgs?.sortBy,
          ...(folder ? { folder } : {}),
        })
      );

      setCombinedFileUrl(mergedDocument.relative_path);
      setCombinedFile(mergedDocument);
      setIsCombineComplete(true);
      setIsCombining(false);
      setCombineProgress(100);
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        firstSerializerError(error?.response?.data) ||
        error?.message ||
        "Failed to combine files";
      setCombineError(message);
      setIsCombining(false);
      setIsCombineComplete(false);
      setCombineProgress(0);
    }
  }, [
    isCombining,
    documents,
    matterId,
    outputName,
    destination,
    onClose,
    dispatch,
    listRefreshArgs,
  ]);

  const handleOpenCombinedFile = useCallback(() => {
    setIsCombining(false);
    setIsCombineComplete(false);
    setCombinedFileUrl(null);
    setCombineProgress(0);
    if (!combinedFile || !combinedFileUrl || !navigate) return;
    const userId = Number(localStorage.getItem("user_id"));
    dispatch(
      postHighlightedMatter({
        matterId: combinedFile.matter,
        docId: combinedFile.unique_id?.substring(1),
        docType: "document",
        tabAction: {
          usr_id: userId,
          document_id: String(combinedFile.unique_id?.substring(1)),
          matter_id: String(combinedFile.matter),
          action: "open",
        },
      })
    ).then(() => {
      navigate(appendAIPaneFromCurrentUrl(`/matter${combinedFileUrl}`));
    });
  }, [combinedFile, combinedFileUrl, dispatch, navigate]);

  const handleCloseNotification = useCallback(() => {
    setIsCombining(false);
    setIsCombineComplete(false);
    setCombinedFileUrl(null);
    setCombineProgress(0);
    setCombineError(null);
  }, []);

  const previewNames = documents.slice(0, 5).map((doc) => doc.name || "Untitled");
  const extraCount = documents.length - previewNames.length;

  return (
    <>
      <Modal
        open={open}
        title="Combine files"
        okText="Combine"
        okButtonProps={{
          disabled: documents.length < 2,
          "data-testid": "combine-files-submit",
        }}
        onOk={handleCombine}
        onCancel={onClose}
        destroyOnClose
        data-testid="combine-files-modal"
      >
        <Form layout="vertical">
          <Form.Item
            label={`Combining ${documents.length} file${documents.length === 1 ? "" : "s"}`}
            style={{ marginBottom: 8 }}
          >
            <Text data-testid="combine-files-preview">
              {previewNames.join(", ")}
              {extraCount > 0 ? `, and ${extraCount} more` : ""}
            </Text>
          </Form.Item>
          <Form.Item label="New file name (optional)">
            <Input
              value={outputName}
              onChange={(e) => setOutputName(e.target.value)}
              placeholder={defaultOutputName()}
              maxLength={200}
              data-testid="combine-files-name"
            />
          </Form.Item>
          <Form.Item label="Place in" required>
            <TreeSelect
              treeData={folderOptions}
              value={destination}
              onChange={(value) => setDestination(value)}
              placeholder="Choose a destination folder"
              showSearch
              treeNodeFilterProp="title"
              treeDefaultExpandAll
              style={{ width: "100%" }}
              data-testid="combine-files-destination"
            />
          </Form.Item>
          {submitError && (
            <Text type="danger" data-testid="combine-files-error">
              {submitError}
            </Text>
          )}
        </Form>
      </Modal>

      {(isCombining || isCombineComplete || combineError) && (
        <MergeProgressNotification
          progress={combineProgress}
          isComplete={isCombineComplete}
          mergedFileUrl={combinedFileUrl}
          error={combineError}
          onClose={handleCloseNotification}
          onOpenFile={handleOpenCombinedFile}
          inProgressLabel="Combining files."
          successLabel="Combined successfully"
        />
      )}
    </>
  );
};

CombineFilesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
  matterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rawTree: PropTypes.object,
  currentFolderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  listRefreshArgs: PropTypes.shape({
    currentPage: PropTypes.number,
    sort: PropTypes.string,
    sortBy: PropTypes.string,
  }),
  navigate: PropTypes.func,
};

export default CombineFilesModal;
